import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '../../components/base-components';
import { StatusPill, TrialBadge } from '../../components/SubscriptionStatus';
import {
  useEntitlement,
  useUsageDetail,
  useProfile,
  useUpdateProfile,
} from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { trackEvent } from '../../services/analytics';
import { UsageMeter, visibleMeterKeys } from '../../components/UsageMeter';
import { PageShell } from '../components/PageShell';
import { PageHeader } from '../components/PageHeader';
import { Panel } from '../components/Panel';
import { Skeleton } from '../components/Skeleton';
import {
  BillingSummaryStrip,
  InvoiceHistorySection,
  UpcomingInvoiceSection,
} from '../components/BillingDetails';
import { Badge } from '../components/Badge';
import { ChevronDownIcon } from '../components/icons';
import { cx, FOCUS_RING } from '../components/styles';
import { v2Path } from '../nav';
import { canSeeBuyingTools } from '../useV2Nav';
import { TakeTheTourButton } from '../tour/TakeTheTourButton';

/* ------------------------------------------------------------------ *
 * Logic block copied verbatim from src/pages/ProfilePage.tsx.
 * Only the JSX and the className strings below each `return` are new.
 *
 * UX rework. Every sub-component, hook, mutation and copy string below is v1's;
 * what changed is where the page puts its weight.
 *
 * The page answers two unrelated questions — "who am I" and "what am I being
 * charged" — and v1 stacks them, so the money question is answered across five
 * equally-weighted panels whose single most-asked number (what the next bill
 * comes to) sits at the bottom of the fourth. Three changes:
 *
 *  - `BillingSummaryStrip` — plan, next charge, charge date, as three tiles at
 *    the top of the money column. It reads `useEntitlement` and
 *    `useUpcomingInvoice`, both already loaded by the panels below; react-query
 *    dedupes on the query key, so this adds no request. It is deliberately a
 *    duplicate: the strip is the answer, `UpcomingInvoiceSection` is the
 *    itemisation, and it is gated on `entitlement.tier` exactly like that
 *    section so the two can never disagree about whether to render.
 *  - At `xl` the identity column sticks. v1's grid leaves one ~500px card
 *    beside ~2500px of panels, so the left gutter is empty for most of the
 *    scroll; sticking it means `Edit` and `Sign out` stay reachable instead of
 *    scrolling away.
 *  - Empty `UsageDetailSection` groups sink below the ones with activity. Every
 *    group still renders, with its count, its unit and its disabled header —
 *    only the order changed, and the sort is stable so a page where everything
 *    has activity is byte-identical to v1's order.
 *
 * `Billing history` also became a collapsible panel, open by default and
 * persisted, because 12 invoice rows are reference material on a page people
 * mostly open to edit their name.
 * ------------------------------------------------------------------ */

function initialsFrom(firstName?: string, lastName?: string, email?: string) {
  const f = (firstName || '').trim();
  const l = (lastName || '').trim();
  if (f || l) return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase() || f.charAt(0).toUpperCase();
  return (email || '?').charAt(0).toUpperCase();
}

function formatJoinDate(iso?: string) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function EntitlementBadges() {
  const { data: entitlement } = useEntitlement();
  const badges: React.ReactNode[] = [];
  if (entitlement?.comped) {
    badges.push(
      <Badge key="comped" tone="green">
        Admin / grandfathered
      </Badge>,
    );
  }
  if (entitlement?.reserve_invited) {
    badges.push(
      <Badge key="reserve" tone="violet">
        Reserve invited
      </Badge>,
    );
  }
  if (entitlement?.tier) {
    badges.push(
      <Badge key="tier" tone="blue">
        {entitlement.tier.charAt(0).toUpperCase() + entitlement.tier.slice(1)} plan
      </Badge>,
    );
  }
  if (badges.length === 0) return null;
  return <div className="flex flex-wrap gap-2">{badges}</div>;
}

function AccountInfoSection() {
  const { user, logout } = useAuth();
  const profileQuery = useProfile();
  const profile = profileQuery.data;
  const updateProfile = useUpdateProfile();
  const { showError, showSuccess } = useToast();
  const [signingOut, setSigningOut] = useState(false);

  const [editing, setEditing] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [touched, setTouched] = useState(false);

  // Seed the form whenever the profile loads or we (re)enter edit mode.
  useEffect(() => {
    setFirstName(profile?.first_name || '');
    setLastName(profile?.last_name || '');
  }, [profile?.first_name, profile?.last_name, editing]);

  const email = profile?.email || user?.email || '';
  const displayName = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ').trim();
  const nameMissing = !displayName;
  const joinedLabel = formatJoinDate(profile?.date_joined);

  const firstError = touched && !firstName.trim() ? 'First name is required' : '';
  const lastError = touched && !lastName.trim() ? 'Last name is required' : '';
  const canSave = !!firstName.trim() && !!lastName.trim() && !updateProfile.isPending;

  const startEdit = () => {
    setTouched(false);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setTouched(false);
  };

  const handleSave = async () => {
    setTouched(true);
    if (!firstName.trim() || !lastName.trim()) return;
    try {
      await updateProfile.mutateAsync({ first_name: firstName.trim(), last_name: lastName.trim() });
      trackEvent('profile_updated');
      showSuccess('Profile updated');
      setEditing(false);
      setTouched(false);
    } catch (err: any) {
      showError(err?.message || 'Could not update profile');
    }
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      trackEvent('user_logged_out');
      await logout();
    } catch (err: any) {
      setSigningOut(false);
      showError(err?.message || 'Sign out failed');
    }
  };

  return (
    <Panel>
      {/* Identity header */}
      <div className="flex items-center gap-4">
        <div className="flex h-14 w-14 flex-shrink-0 select-none items-center justify-center rounded-full bg-gradient-to-br from-[#3b82f6] to-[#8b5cf6] text-lg font-semibold text-white">
          {initialsFrom(profile?.first_name, profile?.last_name, email)}
        </div>
        <div className="min-w-0 flex-1">
          {nameMissing ? (
            <button
              onClick={startEdit}
              className={cx(
                'rounded text-base font-semibold text-[#60a5fa] hover:text-[#93c5fd]',
                FOCUS_RING,
              )}
            >
              Add your name
            </button>
          ) : (
            <div className="truncate text-base font-semibold text-white">{displayName}</div>
          )}
          <div className="truncate text-sm text-white/50">{email || '—'}</div>
        </div>
        {!editing && (
          <button
            onClick={startEdit}
            className={cx(
              'flex-shrink-0 rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-sm font-medium text-white/80 transition-colors hover:bg-white/10',
              FOCUS_RING,
            )}
          >
            Edit
          </button>
        )}
      </div>

      <div className="mt-4">
        <EntitlementBadges />
      </div>

      {/* Details / edit form */}
      <div className="mt-5 border-t border-white/[0.06] pt-5">
        {editing ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              error={!!firstError}
              errorMessage={firstError}
              placeholder="Jane"
              autoFocus
            />
            <Input
              label="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              error={!!lastError}
              errorMessage={lastError}
              placeholder="Doe"
            />
            <div className="sm:col-span-2">
              <div className="mb-1 text-sm text-white/50">Email</div>
              <div className="break-all text-sm text-white/70">{email || '—'}</div>
              <p className="mt-1 text-xs text-white/40">
                Your email is used to sign in and can't be changed here. Contact support to update
                it.
              </p>
            </div>
            <div className="flex flex-wrap justify-end gap-2 sm:col-span-2">
              <button
                onClick={cancelEdit}
                disabled={updateProfile.isPending}
                className={cx(
                  'rounded-lg border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium text-white/70 transition-colors hover:bg-white/10 disabled:opacity-50',
                  FOCUS_RING,
                )}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!canSave}
                className={cx(
                  'rounded-lg bg-[#3b82f6] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#2563eb] disabled:cursor-not-allowed disabled:opacity-50',
                  FOCUS_RING,
                )}
              >
                {updateProfile.isPending ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wide text-white/40">Name</div>
              {nameMissing ? (
                <button
                  onClick={startEdit}
                  className={cx('mt-1 rounded text-sm text-[#60a5fa] hover:text-[#93c5fd]', FOCUS_RING)}
                >
                  Not set — add your name
                </button>
              ) : (
                <div className="mt-1 text-sm text-white">{displayName}</div>
              )}
            </div>
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wide text-white/40">Email</div>
              <div className="mt-1 break-all text-sm text-white">{email || '—'}</div>
            </div>
            {joinedLabel && (
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-wide text-white/40">Member since</div>
                <div className="mt-1 text-sm text-white">{joinedLabel}</div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-5 flex justify-end border-t border-white/[0.06] pt-4">
        <button
          onClick={handleSignOut}
          disabled={signingOut}
          className={cx(
            'rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-300 transition-colors hover:bg-red-500/20 disabled:opacity-50',
            FOCUS_RING,
          )}
        >
          {signingOut ? 'Signing out…' : 'Sign out'}
        </button>
      </div>
    </Panel>
  );
}

function ProfileCompletenessNudge() {
  const { data: profile } = useProfile();
  // Only nudge once we actually have profile data and the name is blank.
  if (!profile) return null;
  const nameMissing = !([profile.first_name, profile.last_name].filter(Boolean).join('').trim());
  if (!nameMissing) return null;

  return (
    <div className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3">
      <div className="mt-0.5 flex-shrink-0 text-amber-300">ℹ️</div>
      <div className="text-sm text-amber-100/90">
        <span className="font-medium text-amber-200">Finish setting up your profile.</span>{' '}
        Add your name so transfers and receipts show who you are. You can do it in the Account info card below.
      </div>
    </div>
  );
}

function SubscriptionSection() {
  const { data: entitlement, isLoading: entLoading } = useEntitlement();

  if (entLoading) {
    return (
      <Panel title="Subscription">
        <div className="space-y-3">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-3.5 w-44" />
        </div>
      </Panel>
    );
  }

  if (entitlement?.comped && !entitlement.tier) {
    return (
      <Panel title="Subscription">
        <div className="flex flex-wrap items-start gap-3">
          <Badge tone="green">Admin access</Badge>
          <p className="min-w-0 flex-1 text-sm text-white/70">
            Billing bypassed — all features unlocked, no usage limits.
          </p>
        </div>
      </Panel>
    );
  }

  if (!entitlement?.tier) {
    return (
      <Panel title="Subscription">
        <p className="mb-4 text-sm text-white/70">You don't have an active subscription.</p>
        <Link
          to={v2Path('/billing')}
          className={cx(
            'inline-block rounded-lg bg-[#3b82f6] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#2563eb]',
            FOCUS_RING,
          )}
        >
          Choose a plan
        </Link>
      </Panel>
    );
  }

  const periodEnd = entitlement.current_period_end
    ? new Date(entitlement.current_period_end).toLocaleDateString()
    : null;

  // Compact summary only — plan switching, payment method and invoices all live
  // on the Billing page so there's a single source of truth.
  return (
    <Panel title="Subscription">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-lg font-bold capitalize text-white">{entitlement.tier}</span>
            <StatusPill status={entitlement.status} />
            <TrialBadge
              status={entitlement.status}
              trialEnd={entitlement.trial_end}
              currentPeriodEnd={entitlement.current_period_end}
            />
          </div>
          {entitlement.cancel_at_period_end && periodEnd ? (
            <span className="text-xs text-amber-400">Cancels {periodEnd}</span>
          ) : entitlement.status === 'active' && periodEnd ? (
            <span className="text-xs text-white/40">Renews {periodEnd}</span>
          ) : null}
        </div>
        <Link
          to={v2Path('/billing')}
          className={cx(
            'flex-shrink-0 rounded-lg border border-[#3b82f6]/40 bg-[#3b82f6]/15 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#3b82f6]/25',
            FOCUS_RING,
          )}
        >
          Manage plan →
        </Link>
      </div>
    </Panel>
  );
}


function UsageSection() {
  const { data: entitlement } = useEntitlement();
  if (!entitlement?.tier) return null;
  const rates = entitlement.metered_rates ?? {};
  const caps = entitlement.trial_caps ?? {};
  const meterKeys = visibleMeterKeys(entitlement.limits, entitlement.usage, rates, caps);
  if (meterKeys.length === 0) return null;

  return (
    <Panel title="Usage this period">
      <div className="grid gap-3 md:grid-cols-2">
        {meterKeys.map(key => (
          <UsageMeter
            key={key}
            meterKey={key}
            used={entitlement.usage[key] ?? 0}
            included={entitlement.limits[key] ?? 0}
            rate={rates[key]}
            trialCap={caps[key]}
          />
        ))}
      </div>
    </Panel>
  );
}



/** One expandable itemised list inside the usage-detail card. */
function DetailGroup({
  title,
  count,
  unit,
  note,
  truncated,
  children,
}: {
  title: string;
  count: number;
  unit: string;
  note?: string;
  truncated?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden rounded-lg border border-white/[0.08]">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        disabled={count === 0}
        aria-expanded={count > 0 ? open : undefined}
        className={cx(
          'flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-white/[0.03] disabled:cursor-default disabled:hover:bg-transparent',
          FOCUS_RING,
        )}
      >
        <span className="min-w-0">
          <span className="text-sm font-medium text-white">{title}</span>
          {note && <span className="mt-0.5 block text-xs text-white/45">{note}</span>}
        </span>
        <span className="flex shrink-0 items-center gap-2">
          <span className="text-sm font-semibold text-white tabular-nums">{count}</span>
          <span className="text-xs text-white/45">{unit}</span>
          {count > 0 && (
            <ChevronDownIcon
              className={cx('h-3.5 w-3.5 text-white/40 transition-transform', open && 'rotate-180')}
            />
          )}
        </span>
      </button>
      {open && count > 0 && (
        <div className="max-h-80 overflow-y-auto border-t border-white/[0.08]">
          {children}
          {truncated && (
            <p className="border-t border-white/[0.06] px-4 py-2 text-xs text-white/40">
              Showing the most recent entries only — see your invoice for the full period.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Groups with activity first. Every group still renders — an empty one keeps its
 * `0`, its unit and its disabled header, which is how you tell "nothing this
 * period" from "not billed for this at all". `sort` is stable, so when every
 * group has activity the order is v1's exactly.
 */
function orderGroups(groups: React.ReactElement<{ count: number }>[]) {
  return [...groups].sort(
    (a, b) => (a.props.count > 0 ? 0 : 1) - (b.props.count > 0 ? 0 : 1),
  );
}

// Exported so Billing can render the same panel. It is the itemisation behind
// the invoice, so it belongs in the billing area at least as much as here —
// and duplicating it would give two lists that drift.
export function UsageDetailSection() {
  const { data: entitlement } = useEntitlement();
  const { data, isLoading, error } = useUsageDetail();

  // Dice accounts are billed even with no FlashTools tier, so this panel is
  // useful to tierless users too — don't gate it on entitlement.tier.
  const joins = data?.waitlist_joins;
  const tickets = data?.ticket_purchases;
  const accounts = data?.dice_accounts;
  const identified = data?.tickets_identified;
  const seats = data?.seats;
  const hasAnything =
    (joins?.total ?? 0) +
      (tickets?.total ?? 0) +
      (accounts?.total ?? 0) +
      (identified?.total ?? 0) +
      (seats?.total ?? 0) >
    0;

  if (!entitlement && !hasAnything) return null;

  // Flash Buy is invite-only. An account that cannot see it must not be shown
  // a "Tickets purchased — charged per ticket by Flash Buy" row, even at 0.
  // Real purchases always show: a lapsed Reserve account still owes for them.
  const showTickets = canSeeBuyingTools(entitlement) || (tickets?.total ?? 0) > 0;

  const since = data?.period_start
    ? new Date(data.period_start).toLocaleDateString()
    : null;

  return (
    <Panel title="Usage detail" hint={since ? `Since ${since}` : undefined}>
      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : error ? (
        <p className="text-sm text-white/60">Could not load usage detail.</p>
      ) : !hasAnything ? (
        <p className="text-sm text-white/60">No billable activity yet this period.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {orderGroups([
          <DetailGroup
            key="joins"
            title="Waitlists joined"
            count={joins?.total ?? 0}
            unit="joins"
            truncated={joins?.truncated}
          >
            {(joins?.items ?? []).map((j, i) => (
              <div key={i} className="border-b border-white/[0.05] px-4 py-2.5 last:border-0">
                <div className="flex justify-between gap-3">
                  <span className="truncate text-sm text-white">{j.event_name}</span>
                  <span className="shrink-0 text-xs text-white/50 tabular-nums">
                    ×{j.number_of_tickets}
                  </span>
                </div>
                <div className="mt-0.5 truncate text-xs text-white/45">
                  {j.ticket_type_name && <>{j.ticket_type_name} · </>}
                  {j.account} · {new Date(j.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </DetailGroup>,

          ...(showTickets ? [
          <DetailGroup
            key="tickets"
            title="Tickets purchased"
            count={tickets?.total ?? 0}
            unit="tickets"
            note="Charged per ticket by Flash Buy"
            truncated={tickets?.truncated}
          >
            {(tickets?.items ?? []).map((t, i) => (
              <div key={i} className="border-b border-white/[0.05] px-4 py-2.5 last:border-0">
                <div className="flex justify-between gap-3">
                  <span className="truncate text-sm text-white">{t.event_name}</span>
                  <span className="shrink-0 text-xs text-white/50 tabular-nums">×{t.quantity}</span>
                </div>
                <div className="mt-0.5 truncate text-xs text-white/45">
                  {t.ticket_name && <>{t.ticket_name} · </>}
                  {t.account} · {new Date(t.created_at).toLocaleDateString()}
                </div>
                {t.ticket_ids.length > 0 && (
                  <div className="mt-1 break-all font-mono text-[11px] text-white/35">
                    {t.ticket_ids.join(', ')}
                  </div>
                )}
              </div>
            ))}
          </DetailGroup>,
          ] : []),

          <DetailGroup
            key="identified"
            title="Tickets identified"
            count={identified?.total ?? 0}
            unit="tickets"
            note="All tickets found in your connected accounts this period"
            truncated={identified?.truncated}
          >
            {(identified?.items ?? []).map((t, i) => (
              <div key={i} className="border-b border-white/[0.05] px-4 py-2.5 last:border-0">
                <div className="flex justify-between gap-3">
                  <span className="truncate text-sm text-white">{t.event_name}</span>
                  {t.face_value != null && (
                    <span className="shrink-0 text-xs text-white/50 tabular-nums">
                      ${t.face_value.toFixed(2)}
                    </span>
                  )}
                </div>
                <div className="mt-0.5 truncate text-xs text-white/45">
                  {t.ticket_type && <>{t.ticket_type} · </>}
                  {t.platform}
                  {t.account && <> · {t.account}</>}
                  {' · '}
                  {new Date(t.purchase_date ?? t.created_at).toLocaleDateString()}
                </div>
                {t.external_ticket_id && (
                  <div className="mt-1 break-all font-mono text-[11px] text-white/35">
                    {t.external_ticket_id}
                  </div>
                )}
              </div>
            ))}
          </DetailGroup>,

          <DetailGroup
            key="accounts"
            title="Dice accounts & numbers"
            count={accounts?.total ?? 0}
            unit="accounts"
            note={
              accounts?.unit_price
                ? `$${accounts.unit_price.toFixed(2)} per account per month`
                : undefined
            }
          >
            {(accounts?.items ?? []).map((a, i) => (
              <div key={i} className="border-b border-white/[0.05] px-4 py-2.5 last:border-0">
                <div className="flex justify-between gap-3">
                  <span className="truncate text-sm text-white">{a.label}</span>
                  <span className="shrink-0 font-mono text-xs text-white/50">{a.phone_number}</span>
                </div>
                {a.forwarding_email && (
                  <div className="mt-0.5 truncate text-xs text-white/45">
                    SMS forwarded to {a.forwarding_email}
                  </div>
                )}
              </div>
            ))}
          </DetailGroup>,

          <DetailGroup
            key="seats"
            title="Team"
            // `active` is what recurs monthly; `total` is everyone ever added.
            // The count shown is the billed one, and the note explains the gap
            // when there is one — otherwise a customer reads the removed rows
            // below as things they are still paying for.
            count={seats?.active ?? 0}
            unit="members"
            note={
              seats && seats.total > seats.active
                ? `$5.00 per member per month · ${seats.total - seats.active} removed`
                : seats?.total
                  ? '$5.00 per member per month'
                  : undefined
            }
          >
            {(seats?.items ?? []).map((m, i) => (
              <div key={i} className="border-b border-white/[0.05] px-4 py-2.5 last:border-0">
                <div className="flex justify-between gap-3">
                  <span
                    className={cx(
                      'truncate text-sm',
                      m.status === 'revoked' ? 'text-white/45 line-through' : 'text-white',
                    )}
                  >
                    {m.label}
                  </span>
                  <span className="shrink-0 text-xs text-white/50 tabular-nums">
                    ${m.monthly_price.toFixed(2)}/mo
                  </span>
                </div>
                <div className="mt-0.5 truncate text-xs text-white/45">
                  {m.label !== m.email && <>{m.email} · </>}
                  Added {new Date(m.added_at).toLocaleDateString()}
                  {m.removed_at && <> · Removed {new Date(m.removed_at).toLocaleDateString()}</>}
                  {!m.removed_at && !m.has_signed_in && <> · Not signed in yet</>}
                </div>
              </div>
            ))}
          </DetailGroup>,
          ])}
        </div>
      )}
    </Panel>
  );
}

export const ProfileScreen: React.FC = () => {
  return (
    <PageShell>
      <PageHeader
        title="Profile"
        subtitle="Manage your account details, subscription, payment method, and billing history."
        actions={<TakeTheTourButton />}
      />
      <div className="space-y-6">
        <ProfileCompletenessNudge />
        <div className="grid gap-6 xl:grid-cols-12 xl:items-start">
          {/* Left: identity. Sticky at `xl` so `Edit` and `Sign out` stay
              reachable while the taller money column scrolls past. */}
          <div
            data-tour="profile-account"
            className="space-y-6 xl:col-span-5 xl:sticky xl:top-6 xl:self-start xl:max-h-[calc(100vh-3rem)] xl:overflow-y-auto xl:pr-1"
          >
            <AccountInfoSection />
          </div>
          {/* Right: money — the answer first, then the panels that explain it */}
          <div className="space-y-6 xl:col-span-7" data-tour="profile-billing">
            <BillingSummaryStrip />
            <SubscriptionSection />
            <UsageSection />
            <UsageDetailSection />
            <UpcomingInvoiceSection />
            <InvoiceHistorySection />
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default ProfileScreen;
